import { Icon, Button } from '@/components/atoms';

export interface TableColumn<T> {
  key: keyof T | string;
  header: string;
  render?: (row: T) => React.ReactNode;
  className?: string;
}

interface ReportTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  emptyMessage?: string;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    onPageChange: (offset: number) => void;
  };
}

export function ReportTable<T extends object>({
  columns,
  data,
  isLoading,
  error,
  onRetry,
  emptyMessage = 'No data available',
  pagination,
}: ReportTableProps<T>) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={48} className="text-neutral-400 mx-auto mb-4 animate-spin" />
            <p className="text-lg font-medium text-neutral-600">Loading report data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="TriangleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">Failed to Load Report</h3>
              <p className="text-error-700 mb-3">
                {error.message || 'Unable to fetch report data. Please try again.'}
              </p>
              {onRetry && (
                <Button variant="primary" size="sm" onClick={onRetry}>
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Retry
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getValue = (row: T, key: string): unknown => {
    const keys = key.split('.');
    let value: unknown = row;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return undefined;
      }
    }
    return value;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              {columns.map((col) => (
                <th
                  key={String(col.key)}
                  className={`px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider ${col.className || ''}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <Icon name="FileSearch" size={48} className="text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-500">{emptyMessage}</p>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={(row as { id?: string }).id || rowIndex}
                  className="hover:bg-neutral-50 transition-colors"
                >
                  {columns.map((col) => (
                    <td
                      key={String(col.key)}
                      className={`px-6 py-4 whitespace-nowrap text-sm ${col.className || ''}`}
                    >
                      {col.render ? col.render(row) : String(getValue(row, String(col.key)) ?? '-')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && data.length > 0 && (
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
          <div className="text-sm text-neutral-600">
            Showing{' '}
            <span className="font-medium">
              {pagination.offset + 1}-
              {Math.min(pagination.offset + pagination.limit, pagination.total)}
            </span>{' '}
            of <span className="font-medium">{pagination.total}</span> records
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.offset === 0}
              onClick={() =>
                pagination.onPageChange(Math.max(0, pagination.offset - pagination.limit))
              }
            >
              <Icon name="ChevronLeft" size={16} className="mr-1" />
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!pagination.hasMore}
              onClick={() => pagination.onPageChange(pagination.offset + pagination.limit)}
            >
              Next
              <Icon name="ChevronRight" size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
